/**
 * Journal Entries list — view persistence.
 *
 * Saves the list's view state (sort, filters, scroll position, how many pages
 * were loaded) when the user opens an entry's detail page, and restores it once
 * when they come back. The snapshot is one-shot and short-lived: it is consumed
 * (cleared) on restore and ignored if older than RESTORE_TTL_MS, so a fresh
 * visit from the menu starts clean rather than jumping to an old scroll spot.
 */

import type { FilterBarCondition } from '@elsapiens/ui'

const STORAGE_KEY = 'je_list_view'
const RESTORE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export interface JournalListView {
  orgId: string
  sortField: string
  sortDirection: 'asc' | 'desc'
  searchQuery: string
  conditions: FilterBarCondition[]
  activeTab: string
  /** scrollTop of the <main> scroll container at navigate-away time. */
  scrollTop: number
  /** The page the user was on, restored on return. */
  currentPage: number
  savedAt: number
}

export function saveJournalListView(view: Omit<JournalListView, 'savedAt'>): void {
  try {
    const payload: JournalListView = { ...view, savedAt: Date.now() }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // sessionStorage unavailable (private mode / quota) — persistence is best-effort.
  }
}

/**
 * Returns the saved view if it is fresh and belongs to the given org, else null.
 * Does not clear it — call clearJournalListView() once the restore is applied.
 */
export function loadJournalListView(orgId: string): JournalListView | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) { return null }
    const view = JSON.parse(raw) as JournalListView
    if (view.orgId !== orgId) { return null }
    if (Date.now() - view.savedAt > RESTORE_TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }
    return view
  } catch {
    return null
  }
}

export function clearJournalListView(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
